import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Flex, toast } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { client } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import {
  AddressType,
  ComplianceExternalObjectType,
  TaxIdType,
} from "~/graphql/supergraphEnums";
import {
  useCreateIndividualProfileAndCreateCaseMutation,
  useCreateInstitutionProfileAndCreateCaseMutation,
  useGetProgramNamesAndIdsQuery,
} from "~/graphql/supergraphHooks";
import {
  type ComplianceIndividualProfileInput,
  type ComplianceInstitutionProfileInput,
  type ExternalObjectInput,
} from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import { toISOFormat } from "~/utils/datetime";
import { VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE } from "../CaseDetailsDrawer/Profiles/common/EntityFields/constants";
import { ExitWarningModal } from "../Common/ExitWarningModal";
import { useCaseFormContext } from "../CreateCaseNavigation/CaseFormContext";
import { useComplianceAiExtraction, useIOReusableProfiles } from "../flags";
import { getClientId, getEntityId } from "../utils";
import { resolveAmlEntityId } from "../utils/amlTypeIdUtil";
import { ObjectTypeEnum, MAIN_SERVICE } from "../utils/constants";
import CaseInformationCard from "./CaseInformationCard";
import { FIELD_MAPPING, INITIAL_VALUES } from "./constants";
import InvestorAccountCard from "./InvestorAccountCard";
import { getSchema } from "./schema";
import { FormValues } from "./types";

type CreateCaseFormProps = {
  extractedData?: Record<string, string>;
  inAiExtractionMode?: boolean;
};

const CreateCaseForm = ({ extractedData, inAiExtractionMode = false }: CreateCaseFormProps) => {
  const history = useHistory();
  const location = useLocation();
  const isAiExtractionEnabled = useComplianceAiExtraction();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const [prospectId, setProspectId] = useState<number | null>();
  const [showCancelWarningModal, setShowCancelWarningModal] = useState(false);
  const caseFormContext = useCaseFormContext();
  const searchParams = new URLSearchParams(location.search);
  const strDocumentId = searchParams.get("documentId");
  const documentId = strDocumentId ? parseInt(strDocumentId) : null;
  const validationSchema = useMemo(
    () => getSchema(ioReusableProfilesEnabled),
    [ioReusableProfilesEnabled],
  );
  const getProspectInputObject: () => ExternalObjectInput | null = () => {
    return prospectId
      ? {
          objectId: prospectId.toString(),
          objectTypeEnum: ComplianceExternalObjectType.Prospect,
          service: MAIN_SERVICE,
        }
      : null;
  };
  const programsResult = useGetProgramNamesAndIdsQuery();
  const programNameIdList = programsResult.data?.programsAssignableToEntities?.map(
    (item): [string, number] => [item.name, item.id],
  );
  const programNameToIdMap: Record<string, number> = Object.fromEntries(
    programNameIdList ?? [],
  );

  const [createIndividualProfileAndCreateCase] =
    useCreateIndividualProfileAndCreateCaseMutation({
      fetchPolicy: "network-only",
      awaitRefetchQueries: true,
      onError: (e) => toast.error(e.message),
      onCompleted: async () => {
        await client.refetchQueries({
          include: "active",
        });
      },
    });

  const [createInstitutionProfileAndCreateCase] =
    useCreateInstitutionProfileAndCreateCaseMutation({
      fetchPolicy: "network-only",
      awaitRefetchQueries: true,
      onError: (e) => toast.error(e.message),
      onCompleted: async () => {
        await client.refetchQueries({
          include: "active",
        });
      },
    });

  /**
   * Submits the individual profile for creation. Currently prints a toast on success or
   * a toast on error.
   *
   * @param profileData The data about the individual to use to create the profile.
   */
  async function addNewIndividualAndCreateCase(
    profileData: ComplianceIndividualProfileInput,
    externalArena: ExternalObjectInput,
    externalEntity: ExternalObjectInput,
    assignToSelf: boolean,
    programName: string | undefined,
  ) {
    try {
      if (!programName) {
        toast.error(
          `Case cannot be created because the entity ${externalEntity.name} does not have a compliance program set`,
        );
        return;
      }

      // Returns an id in the response
      const caseResponse = await createIndividualProfileAndCreateCase({
        variables: {
          profileData,
          externalArena,
          externalEntity,
          caseExternalObject: getProspectInputObject(),
          assignSelf: assignToSelf,
          programId: programNameToIdMap[programName],
          documentIds: documentId ? [documentId] : null,
        },
      });
      const caseId = caseResponse.data?.createCaseWithNewIndividualProfile.case.id;
      if (caseResponse.errors || !caseId) {
        return;
      }

      // Announce success and navigate to the new case
      toast.success("New case created");
      history.push(`${globalEnv.complianceRoute}/case/${caseId}`);
    } catch {
      // do nothing
    }
  }

  /**
   * Submits the institution profile for creation. Currently prints a toast on success or
   * a toast on error.
   *
   * @param data The data about the institution to use to create the profile.
   */
  async function addNewInstitutionAndCreateCase(
    profileData: ComplianceInstitutionProfileInput,
    externalArena: ExternalObjectInput,
    externalEntity: ExternalObjectInput,
    assignToSelf: boolean,
    programName: string | undefined,
  ) {
    try {
      if (!programName) {
        toast.error(
          `Case cannot be created because the entity ${externalEntity.name} does not have a compliance program set`,
        );
        return;
      }
      // Returns an id in the response
      const caseResponse = await createInstitutionProfileAndCreateCase({
        variables: {
          profileData: profileData,
          externalArena,
          externalEntity,
          caseExternalObject: getProspectInputObject(),
          assignSelf: assignToSelf,
          programId: programNameToIdMap[programName],
          documentIds: documentId ? [documentId] : null,
        },
      });
      const caseId = caseResponse.data?.createCaseWithNewInstitutionProfile.case.id;
      if (caseResponse.errors || !caseId) {
        return;
      }

      // Announce success and navigate to the new case
      toast.success("New case created");
      history.push(`${globalEnv.complianceRoute}/case/${caseId}`);
    } catch {
      // do nothing
    }
  }

  /**
   * Handles what happens when the user clicks the submit button. This first creates
   * a profile. This can be either an institution or an individual profile. Then if
   * that was successful, it creates a case linking the profile as the root of that case.
   *
   * @param values The values the user filled out.
   */
  const handleSubmit = async (values: FormValues) => {
    // Generic profile values for both individuals & institutions
    const profileValues = {
      taxId: values.taxId,

      addresses: [
        {
          addressType: AddressType.Unknown, // We should ask for this?
          street: values.streetLine1,
          street2: values.streetLine2,
          city: values.city,
          state: values.stateOrProvince,
          postalCode: values.zipCode,
          country: values.country || "US",
        },
      ],
    };

    const clientId = getClientId(values);
    const entityId = getEntityId(values);

    const externalArena = {
      objectId: clientId!,
      objectTypeEnum: ObjectTypeEnum.Arena,
      service: MAIN_SERVICE,
      name: values.client?.name ?? "",
    };

    const externalEntity = {
      objectId: entityId!,
      objectTypeEnum: ObjectTypeEnum.Account,
      service: MAIN_SERVICE,
      name: values.entity?.name ?? "",
    };

    // Institutions (entities) & individuals call different end points and have different values, so handle
    // each separately
    if (values.accountType === "institution") {
      // Add the fields specific to "entity"
      const trimmedDateOfFormation = values.dateOfFormation
        ? toISOFormat(values.dateOfFormation)
        : undefined;
      const entityValues = {
        accountType: VIEWABLE_ACCOUNT_TO_ACCOUNT_TYPE[values.entityType], // Note the name change; there's a
        // values.accountType, but that refers to whether this is an entity (institution) or an individual
        legalBusinessName: values.entityName,
        dateOfFormation: trimmedDateOfFormation,
        taxIdType: TaxIdType.Ein, // TODO RKT-2250: Don't hardcode
        amlEntityId: resolveAmlEntityId(values.amlEntityType, ioReusableProfilesEnabled),
      };

      const profileData = {
        ...profileValues,
        ...entityValues,
      };

      await addNewInstitutionAndCreateCase(
        profileData,
        externalArena,
        externalEntity,
        values.assignToMyself,
        values.programName,
      );
    } else if (values.accountType === "individual") {
      // Add the fields specific to "individual"
      const trimmedDateOfBirth = values.dateOfBirth
        ? toISOFormat(values.dateOfBirth)
        : undefined;
      const individualValues = {
        firstName: values.firstName,
        middleName: values.middleName,
        lastName: values.lastName,
        dateOfBirth: trimmedDateOfBirth,
        taxIdType: values.taxIdType,
      };

      const profileData = {
        ...profileValues,
        ...individualValues,
      };

      await addNewIndividualAndCreateCase(
        profileData,
        externalArena,
        externalEntity,
        values.assignToMyself,
        values.programName,
      );
    }
  };

  /**
   * Handles what to do if the user exits without submitting a case.
   */
  const onCancelExit = () => {
    if (isAiExtractionEnabled) setShowCancelWarningModal(true);
    else history.push(`${globalEnv.complianceRoute}`);
  };

  const handleConfirmWarning = () => {
    setShowCancelWarningModal(false);
    history.push(`${globalEnv.complianceRoute}`);
  };

  const handleCancelWarning = () => {
    setShowCancelWarningModal(false);
  };

  const mergedInitialValues = useMemo(() => {
    const merged: FormValues = { ...INITIAL_VALUES };

    // First, merge case information before merging extracted data
    if (caseFormContext?.caseInformationData) {
      Object.assign(merged, caseFormContext.caseInformationData);
    }

    (Object.keys(INITIAL_VALUES) as Array<keyof FormValues>).forEach((fieldKey) => {
      const extractedKey = FIELD_MAPPING[fieldKey];
      const extractedValue = extractedKey && extractedData?.[extractedKey];
      if (extractedValue !== undefined && extractedValue !== null && extractedValue !== "") {
        (merged[fieldKey] as typeof extractedValue) = extractedValue;
      }
    });

    return merged;
  }, [extractedData, caseFormContext?.caseInformationData]);

  return (
    <>
      <Form
        initialValues={mergedInitialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        submitOnlyChangedValues={false}
      >
        <Column size={isAiExtractionEnabled ? "md" : "sm"}>
          <CaseInformationCard setProspectId={setProspectId} />
          <InvestorAccountCard inAiExtractionMode={inAiExtractionMode} />
          <Flex justifyContent="flex-end">
            <CancelButton onClick={onCancelExit} css={{ marginRight: SPACING.MEDIUM }} />
            <SubmitButton>Create case</SubmitButton>
          </Flex>
        </Column>
      </Form>
      {isAiExtractionEnabled && (
        <ExitWarningModal
          show={showCancelWarningModal}
          onCancel={handleCancelWarning}
          onConfirm={handleConfirmWarning}
        />
      )}
    </>
  );
};

export default CreateCaseForm;
